import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';

import { apiReducerBuilder } from 'utils';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'utils/constants';

import { logout, refreshToken, login, login2FA } from './actions';
import { confirmEmail } from '../registration/actions';
import { deleteAccountConfirm } from '../userSettings/actions';

interface Authorization {
	id: number;
	status: string;
	tfaToken?: string;
	authError: boolean;
	data: Record<string, any>;
}

const initialState = {} as Authorization;

const setTokens = (payload: { [key: string]: any }) => {
	const accessToken = payload.access_token;
	const refreshToken = payload.refresh_token;
	const decodedRefreshToken = jwtDecode<any>(refreshToken);
	const refreshTokenExpiration = new Date(decodedRefreshToken.exp * 1000);

	Cookies.set(ACCESS_TOKEN, accessToken, { expires: refreshTokenExpiration });
	Cookies.set(REFRESH_TOKEN, refreshToken, { expires: refreshTokenExpiration });
};

const fulfilledCallback = (state: Authorization, action: { [key: string]: any }) => {
	const { payload, meta }: { [key: string]: any } = action;

	if (payload.tfa_is_active) {
		state.tfaToken = payload.tfa_token;
	} else {
		setTokens(payload);
	}

	state.status = meta.requestStatus;
	if (payload?.access_token) {
		state.data = jwtDecode<any>(payload.access_token);
	}
};

const authorizationSlice = createSlice({
	name: 'authorization',
	initialState,
	reducers: {
		initAuth(state, { payload }) {
			if (payload?.access_token) {
				state.data = jwtDecode<any>(payload?.access_token);
			}
		},
		setAuthAdmin(state, action) {
			setTokens(action.payload);
		},
		setAuthError(state, action) {
			state.authError = action.payload;
		},
	},
	extraReducers: (builder) => {
		apiReducerBuilder(builder, login, fulfilledCallback);
		apiReducerBuilder(builder, refreshToken, fulfilledCallback);
		apiReducerBuilder(builder, login2FA, fulfilledCallback);
		builder.addCase(confirmEmail.fulfilled, fulfilledCallback);

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), () => {
			Cookies.remove(ACCESS_TOKEN);
			Cookies.remove(REFRESH_TOKEN);
			return initialState;
		});
	},
});

export const { setAuthAdmin, setAuthError, initAuth } = authorizationSlice.actions;

export default authorizationSlice.reducer;
